import {Injectable}              from '@angular/core';
import {TranslationStore}        from '../../stores/translation/translation.store';
import moment                    from 'moment';
import {LanguageStore}           from '../../stores/translation/language.store';
import {FertilizerStore}         from '../../stores/base-data/fertilizer.store';
import {CropApplicationStore}    from '../../stores/task-management/crop-application.store';
import {CropTypeStore}           from '../../stores/base-data/crop.types.store';
import {NFertilizationStore}     from '../../stores/n-fertilization/n-fertilization.store';
import {FertilizerService}       from '../../services/data/fertilizer.service';
import {NFertilizationService}   from '../../services/data/n-fertilization.service';
import {GetOperationModeService} from '../../ap-utils/service/ap-get-operationmode.service';
import {MapStore}                from '../../stores/map/map.store';
import {ApNutrientService}       from '../../ap-utils/service/ap-nutrient.service';
import {ApTranslationService}    from '../../ap-utils/service/ap-translation.service';
import {FieldStore}              from '../../stores/farm/field.store';
import {ISummaryWithPlanBooks}   from '../types/n-fertilization-common.types';
import {IGridStatus}             from '../../ap-interface/interfaces/ap-grid-status.interface';
import {ApColumnState}           from '../../ap-interface/enums/ap-column-state.enum';
import {ObjectFactory}           from 'ts-tooling';
import {LogfileService}          from '../../services/data/logfiles.service';
import INFertilizationPlanBook = Data.TaskManagement.INFertilizationPlanBook;
import IDatasetFieldMap = Data.Sensor.IDatasetFieldMap;
import IField = Data.FieldManagement.IField;
import IBasicFertilisationPlanBooks = Data.TaskManagement.IBasicFertilisationPlanBooks;

@Injectable({providedIn: 'root'})
export class ApNFertilizationComponentService {
  constructor(private mapStore: MapStore,
              private fieldStore: FieldStore,
              private cropStore: CropTypeStore,
              private fertilizerStore: FertilizerStore,
              private languageStore: LanguageStore,
              private translationStore: TranslationStore,
              private nFertilizationStore: NFertilizationStore,
              private cropApplicationStore: CropApplicationStore,
              private logfileService: LogfileService,
              private nutrientService: ApNutrientService,
              private fertilizerService: FertilizerService,
              private translationService: ApTranslationService,
              private nFertilizationService: NFertilizationService,
              private operationModeService: GetOperationModeService) {
  }

  /**
   * Converts fertilization plan books for use with logfiles, optionally adding a new plan entry.
   * @param logfiles - An array of dataset field maps representing logfiles, including metadata such as FieldGeomId.
   * @param planBooks - An array of fertilization plan books to filter and process.
   * @param withNewPlan - A flag indicating whether to include a new planning entry in the results.
   * @return An array of fertilization plan books filtered and augmented for logfile compatibility,
   * including an optional new plan entry if specified.
   * @public
   */
  public convertPlansForLogfiles(logfiles: IDatasetFieldMap[], planBooks: INFertilizationPlanBook[],
                                 withNewPlan: boolean = false): INFertilizationPlanBook[] {
    const resultList = [];
    if (withNewPlan) {
      resultList.push({
        Id: '0',
        Note: `(${this.translationStore.FindTranslationForSelectedLanguage('Global__New_Planning')})`
      });
    }
    const logfilesFieldGeom = logfiles.map(x => x.FieldGeomId);
    const logfilesLoggerIds = logfiles.map(x => {
      const logFileOperationMode = this.logfileService.getOperationModeFromHeaders(x);
      return this.operationModeService.getOperationModeByLoggerId(logFileOperationMode).LoggerId;
    });
    const filteredPlans = planBooks.filter(planBook => logfilesFieldGeom.some(x => x === planBook.FieldGeomId));
    const filteredPlanBooks = filteredPlans
      .filter(pb => logfilesLoggerIds.filter(x => x === pb.LogFileApplicationMode))
      .map((planBook) => {
        const result = ObjectFactory.Copy(planBook);
        result.Note = this.getPlanningDescription(planBook);
        return result;
      });
    resultList.push(...filteredPlanBooks);
    return resultList;
  }

  public getPlanningDescription(planBook: INFertilizationPlanBook): string {
    const token = [];
    if (planBook.MainCropId && planBook.ECValue) {
      const application = this.cropApplicationStore.GetApplication(
        this.cropStore.getDefaultCroptype(planBook.MainCropId).Eppocode, planBook.ECValue, planBook.OperationMode);
      const translation = this.translationStore.FindTranslationForSelectedLanguage('Global__N_ApplNum');
      token.push(`${application} ${translation}`);
    }
    if (!!planBook.ProductId || planBook.ProductId === 0) {
      const fertilizer = this.fertilizerStore.getFertilizer(planBook.ProductId);
      token.push(this.fertilizerService.getFertilizerProductName(fertilizer));
    }
    if (planBook?.ApplicationMode?.Key === 'Global__RateConst') {
      token.push(this.translationStore.FindTranslationForSelectedLanguage('Global__RateConst'));
    } else if (planBook?.ApplicationMode?.Key === 'Global__RateVariable') {
      token.push(this.translationStore.FindTranslationForSelectedLanguage('Global__RateVariable'));
    }
    if (planBook.ApplicationDate) {
      token.push(moment(planBook.ApplicationDate).format(this.languageStore.SelectedLanguage.DateFormat));
    }
    if (planBook.Summary?.Title) {
      token.push(planBook.Summary.Title);
    }
    return token.Join(' ');
  }

  public getProductIdByNContent(content: number): number {
    let min = 100 - content;
    let organic: boolean;
    let productId = 0;
    this.fertilizerStore.Fertilizer.filter(f => f.ContentN > 0).forEach((f) => {
      if (!(min < Math.abs(f.ContentN * (f.IsOrganic ? 0.1 : 1) - content))) {
        min = Math.abs(f.ContentN - content);
        productId = f.Id;
        organic = f.IsOrganic;
      } else if (min === Math.abs(f.ContentN - content) && organic && !f.IsOrganic) {
        productId = f.Id;
        organic = f.IsOrganic;
      }
    });
    return productId;
  }

  public getFieldApplicationRateValues(field: IField, planBooks: INFertilizationPlanBook[], logfiles: IDatasetFieldMap[], baseFertPlanBooks: IBasicFertilisationPlanBooks[]): boolean[] {
    const contentNPlanBooks = this.nFertilizationService.getContentNRelatedPlanBooks(field, planBooks, logfiles, baseFertPlanBooks);
    if (!contentNPlanBooks || contentNPlanBooks.length <= 0) {
      return [];
    }
    const applicationsRate = this._getApplicationsRate(contentNPlanBooks);
    return [
      applicationsRate.some(appl => appl === 0),
      applicationsRate.some(appl => appl === -1),
      applicationsRate.some(appl => appl === 1),
      applicationsRate.some(appl => appl === 2),
      applicationsRate.some(appl => appl === 3),
      applicationsRate.some(appl => appl === 4),
    ];
  }

  public getApplicationRateFromPlanBooks(planBooks: INFertilizationPlanBook[]): number | undefined {
    if (!planBooks || planBooks.length <= 0) {
      return undefined;
    }
    const applications = planBooks.map(x => ({
      CropTypeId: x.MainCropId,
      OperationModeId: x.OperationMode.Id,
      EcValue: x.ECValue
    }));
    const applicationsRate = this._getApplicationsRate(applications);
    const singleRate = applicationsRate[0];
    if (!Number.isFinite(singleRate)) {
      return undefined;
    }
    const areRatesTheSame = applicationsRate.every(num => num === undefined || num === singleRate);
    if (!areRatesTheSame) {
      console.warn('N-Planbooks from Summary have more then one Rate');
      return undefined;
    }
    return singleRate;
  }

  public getApplicationMinMax(cropKey: number, application: number | null | undefined): { min: number, max: number } {
    if (application === null || application === undefined) {
      return {min: undefined, max: undefined};
    }
    const applications = this.nFertilizationStore.Listen((s) => s.cropEcSn).getValue();
    const sorted = applications.filter((a) => a.CropKeys.includes(cropKey)).sort((a, b) => a.EcMin - b.EcMin);
    if (sorted.length > application) {
      return {min: sorted[application].EcMin, max: sorted[application].EcMax};
    }
    return {min: undefined, max: undefined};
  }

  public getPlanBooksFieldsCount(planBooks: INFertilizationPlanBook[], isBooked: boolean): string {
    if (!planBooks || planBooks.length <= 0) {
      return '0/0';
    }
    const nonDeletedPlanBooks = planBooks.filter(x => !x.DeletedAt);
    const filteredPlanBooks = planBooks.filter(x => isBooked ? x.BookedAt : !x.BookedAt);
    return filteredPlanBooks.length + '/' + nonDeletedPlanBooks.length;
  }

  public showPlanBookStatisticOnMap(planBooks: INFertilizationPlanBook[], currentUrl: string): void {
    if (!currentUrl) {
      return;
    }
    this.mapStore.Layers.NApplicationMapLayer.clear();
    this.mapStore.Layers.Clear();
    this.mapStore.Legends.removeLegend(this.mapStore.Legends.NPlanningLegend);
    const ids = planBooks.map(x => x.Id);
    if (ids.length === 0) {
      return;
    }
    // TODO this is missing in the data model
    const statistic = {
      Max: planBooks.Reduce((target, ele) => {
        if (ele.ApplicationMax > target) {
          return ele.ApplicationMax;
        }
        return target;
      }, 0),
      ElementId: 5,
      ProductId: planBooks.FirstOrDefault().ProductId,
      Min: planBooks.Reduce((target, ele) => {
        return target < ele.ApplicationMin ? target : ele.ApplicationMin;
      }, planBooks.FirstOrDefault().ApplicationMin),
      Average: planBooks.FirstOrDefault().ApplicationRate
    };
    this.mapStore.Legends.onNPlanningChanged.emit(statistic);
    this.mapStore.Layers.NPlanningLayerSourceInit(currentUrl, ids);
  }

  public getCropId(planBooks: INFertilizationPlanBook[]): number | undefined {
    const filteredData = planBooks.map(pB => pB.MainCropId).reduce((acc, el) => {
      acc.k[el] = acc.k[el] ? acc.k[el] + 1 : 1;
      acc.max = acc.max ? acc.max < acc.k[el] ? el : acc.max : el;
      return acc;
    }, {k: {}, max: undefined});
    return filteredData.max;
  }

  public getProductId(planBooks: INFertilizationPlanBook[]): number | undefined {
    const filteredData = planBooks.map(pB => pB.ProductId).reduce((acc, el) => {
      acc.k[el] = acc.k[el] ? acc.k[el] + 1 : 1;
      acc.max = acc.max ? acc.max < acc.k[el] ? el : acc.max : el;
      return acc;
    }, {k: {}, max: undefined});
    return filteredData.max;
  }

  public getSummaryWithPlanBooks(planBooks: INFertilizationPlanBook[], isBooking: boolean): ISummaryWithPlanBooks[] {
    const summariesMap = planBooks.reduce((summaryMap, item) => {
      const id = item.Summary?.Id?.toString() ?? undefined;
      if (id === undefined) {
        return summaryMap;
      }
      if (summaryMap[id] === undefined) {
        summaryMap[id] = {Summary: item.Summary, FilteredPlanBooks: [], TotalPlanBooks: []};
      }
      if (!item.DeletedAt && ((isBooking && item.BookedAt) || (!isBooking && !item.BookedAt))) {
        summaryMap[id].FilteredPlanBooks.push(item);
      }
      summaryMap[id].TotalPlanBooks.push(item);
      return summaryMap;
    }, {} as Record<string, ISummaryWithPlanBooks>);
    return Object.keys(summariesMap).map((key) => summariesMap[key]);
  }

  public getStatus(s: ISummaryWithPlanBooks, isBooked: boolean): IGridStatus {
    if (!this._isRecordValid(s)) {
      return {
        state: ApColumnState.Red,
        class: 'ap-status-red',
        description: 'Global__Corrupted_Planning_Data'
      };
    }
    return this.nutrientService.getFertilizerStatus(s.Summary.ApplicationMode, isBooked);
  }

  public getArea(planBooks: INFertilizationPlanBook[]): number {
    return planBooks.reduce((totalArea, b) => {
      const fieldGeom = this.fieldStore.getFieldGeomById(b.FieldGeomId);
      const planBookArea = fieldGeom?.AreaHa ?? b.AreaHa ?? 0;
      return totalArea + planBookArea;
    }, 0);
  }

  public applyAdditionalStylesForFertilizationValues(dataItem: { FertilizerEfficiency: number | undefined }): string {
    return this._isDetailItemEfficiencyValid(dataItem) ? ' italic' : '';
  }

  public tooltipForFertilizationValues(dataItem: { FertilizerEfficiency: number | undefined }): string {
    if (!this._isDetailItemEfficiencyValid(dataItem)) {
      return '';
    }
    const nEffectivenessTranslation = this.translationService.translate('Settings__Lbl_NEffectiveness');
    return `${nEffectivenessTranslation}: ${dataItem.FertilizerEfficiency}%`;
  }

  private _isDetailItemEfficiencyValid(dataItem: { FertilizerEfficiency: number | undefined }): boolean {
    return Number.isFinite(dataItem.FertilizerEfficiency)
      && dataItem.FertilizerEfficiency >= 0
      && dataItem.FertilizerEfficiency < 100;
  }

  private _isRecordValid(s: ISummaryWithPlanBooks): boolean {
    return !!s.Summary?.ApplicationMode?.Key && !!s.Summary?.OperationMode?.Key && s.FilteredPlanBooks?.length > 0;
  }

  private _getApplicationsRate(applications: {
    CropTypeId: number,
    OperationModeId: number,
    EcValue: number
  }[]): (number | undefined)[] {
    return applications.map(application => {
      const defaultCrop = this.cropStore.getDefaultCroptype(application.CropTypeId);
      const operationMode = this.operationModeService.getOperationModeById(application.OperationModeId);
      return this.cropApplicationStore.GetApplication(defaultCrop?.Eppocode, application.EcValue, operationMode);
    });
  }
}
